import './welcomeModal.css';

import React from 'react';
import { href } from '@bentley/pw-config';
import { Avatar, Button, Dialog, Flex, Text, Tile } from '@itwin/itwinui-react';
import packageInfo from '../../../package.json';
import { useConfigContext } from '../../context';
import { useAppVersion } from '../../hooks/useAppVersion';
import { t } from '../../services/translation';

type WelcomeModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export function WelcomeModal({
  isOpen,
  onClose
}: WelcomeModalProps): JSX.Element {
  const { config } = useConfigContext();

  const { newFeaturesAvailable } = useAppVersion();

  const isWebView = config.DataAccessLevel == 'Datasource';

  function title() {
    if (newFeaturesAvailable()) {
      return isWebView
        ? t('WelcomeModal.NewFeaturesPWWebView')
        : t('WelcomeModal.NewFeaturesPWWAC');
    }

    return isWebView
      ? t('ProductName.ProjectWiseWebView')
      : t('ProductName.ProjectWiseWeb');
  }

  const infoText = isWebView
    ? t('WelcomeModal.AboutPWWebView')
    : t('WelcomeModal.AboutPWWAC');

  const communitiesLink = isWebView
    ? href.pwWebViewCommunities
    : href.pwWebCommunities;

  const helpTitle = isWebView
    ? t('WelcomeModal.HelpPWWebView')
    : t('WelcomeModal.HelpPWWAC');

  const helpLink = isWebView ? href.pWWebViewHelp : href.pWWebHelp;
  const whatsNewLink = isWebView ? href.pwWebViewWhatsNew : href.pwWebWhatsNew;

  function imageLocation(imageName: string): string {
    return `${location.origin}/${imageName}`;
  }

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      isDraggable={true}
      portal={true}
      preventDocumentScroll={true}
      setFocus={true}
      trapFocus={true}
    >
      <Dialog.Backdrop />
      <Dialog.Main className={'w-m-modal'}>
        <Dialog.TitleBar titleText={title()} isDismissible={false} />
        <Dialog.Content className="w-m-modal-content">
          <Flex flexDirection="column" alignItems="stretch">
            <Text>{infoText}</Text>
            <span className="w-m-tile-container">
              <Tile.Wrapper className="w-m-tile">
                <Tile.ThumbnailArea>
                  <Tile.ThumbnailPicture>
                    <Avatar
                      size="x-large"
                      image={
                        <img
                          src={imageLocation('BentleyCommunities.ico')}
                          alt={t('Generic.BentleyCommunities')}
                        />
                      }
                    />
                  </Tile.ThumbnailPicture>
                  <Tile.QuickAction />
                </Tile.ThumbnailArea>
                <Tile.Name>
                  <Tile.NameLabel>
                    <Tile.Action
                      href={communitiesLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="BentleyCommunities"
                    >
                      {t('Generic.BentleyCommunities')}
                    </Tile.Action>
                  </Tile.NameLabel>
                </Tile.Name>
              </Tile.Wrapper>
              <Tile.Wrapper className="w-m-tile">
                <Tile.ThumbnailArea>
                  <Tile.ThumbnailPicture>
                    <Avatar
                      size="x-large"
                      image={
                        <img src={imageLocation('Help.ico')} alt={helpTitle} />
                      }
                    />
                  </Tile.ThumbnailPicture>
                  <Tile.QuickAction />
                </Tile.ThumbnailArea>
                <Tile.Name>
                  <Tile.NameLabel>
                    <Tile.Action
                      href={helpLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="Help"
                    >
                      {helpTitle}
                    </Tile.Action>
                  </Tile.NameLabel>
                </Tile.Name>
              </Tile.Wrapper>
              <Tile.Wrapper className="w-m-tile">
                <Tile.ThumbnailArea>
                  <Tile.ThumbnailPicture>
                    <Avatar
                      size="x-large"
                      image={
                        <img
                          src={imageLocation('Design.ico')}
                          alt={t('WelcomeModal.WhatsNew')}
                        />
                      }
                    />
                  </Tile.ThumbnailPicture>
                  <Tile.QuickAction />
                </Tile.ThumbnailArea>
                <Tile.Name>
                  <Tile.NameLabel>
                    <Tile.Action
                      href={whatsNewLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="WhatsNew"
                    >
                      {t('WelcomeModal.WhatsNew')}
                    </Tile.Action>
                  </Tile.NameLabel>
                </Tile.Name>
              </Tile.Wrapper>
            </span>
          </Flex>
        </Dialog.Content>
        <Dialog.ButtonBar>
          <Text variant="small" isMuted={true} className="w-m-version-text">
            {t('Generic.Version.WithVersion', {
              version: packageInfo.version
            })}
          </Text>
          <Button onClick={onClose}>{t('Generic.Close')}</Button>
        </Dialog.ButtonBar>
      </Dialog.Main>
    </Dialog>
  );
}
